import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { openLoginModal } from '../../auth/actions';
import { authApi } from '../../auth/api';
const FloatingSupportButton = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isAuthenticated = useSelector((state) => state['features/auth']?.isAuthenticated ?? false);
    const userInfo = useSelector((state) => state['features/auth']?.userInfo);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('new');
    const [view, setView] = useState('tabs');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
        phoneNumber: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [myTickets, setMyTickets] = useState([]);
    const [isLoadingTickets, setIsLoadingTickets] = useState(false);
    const [currentThread, setCurrentThread] = useState(null);
    const [isLoadingThread, setIsLoadingThread] = useState(false);
    const [replyMessage, setReplyMessage] = useState('');
    const [isSendingReply, setIsSendingReply] = useState(false);
    useEffect(() => {
        if (isModalOpen && isAuthenticated && userInfo?.phone) {
            loadMyTickets();
        }
    }, [isModalOpen, isAuthenticated, userInfo?.phone]);
    const loadMyTickets = async () => {
        if (!userInfo?.phone)
            return;
        setIsLoadingTickets(true);
        try {
            const tickets = await authApi.getMyTickets(userInfo.phone);
            setMyTickets(tickets);
        }
        catch (error) {
            console.error('Failed to load tickets:', error);
        }
        finally {
            setIsLoadingTickets(false);
        }
    };
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!authApi.isAuthenticated()) {
            dispatch(openLoginModal());
            return;
        }
        setIsSubmitting(true);
        try {
            const response = await authApi.submitSupportForm(formData);
            localStorage.setItem(`ticket_${response.id}`, response.accessToken);
            setFormData({ name: '', email: '', subject: '', message: '', phoneNumber: '' });
            toast.success('تیکت شما با موفقیت ثبت شد', {
                position: 'top-center',
                autoClose: 3000,
                rtl: true
            });
            await loadMyTickets();
            setActiveTab('history');
        }
        catch (error) {
            if (error.message === 'TOO_MANY_REQUESTS') {
                toast.error(t('support.tooManyRequests'), {
                    position: 'top-center',
                    autoClose: 5000,
                    rtl: true
                });
            }
            else {
                toast.error('خطا در ارسال تیکت', {
                    position: 'top-center',
                    autoClose: 5000,
                    rtl: true
                });
            }
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleTicketClick = async (ticket) => {
        setIsLoadingThread(true);
        setView('thread');
        try {
            const thread = await authApi.getTicketThread(ticket.accessToken);
            // Store accessToken for this ticket
            localStorage.setItem(`ticket_${thread.id}`, ticket.accessToken);
            setCurrentThread(thread);
        }
        catch (error) {
            toast.error('خطا در بارگذاری تیکت', {
                position: 'top-center',
                autoClose: 3000,
                rtl: true
            });
            setView('tabs');
        }
        finally {
            setIsLoadingThread(false);
        }
    };
    const handleSendReply = async () => {
        if (!replyMessage.trim() || !currentThread)
            return;
        setIsSendingReply(true);
        try {
            const accessToken = localStorage.getItem(`ticket_${currentThread.id}`);
            if (!accessToken) {
                toast.error('توکن دسترسی یافت نشد', { position: 'top-center', autoClose: 3000, rtl: true });
                return;
            }
            const newMessage = await authApi.replyToTicket(accessToken, replyMessage);
            setCurrentThread(prev => prev ? {
                ...prev,
                messages: [...prev.messages, newMessage]
            } : null);
            setReplyMessage('');
            toast.success('پاسخ شما ارسال شد', {
                position: 'top-center',
                autoClose: 2000,
                rtl: true
            });
        }
        catch (error) {
            toast.error(error.message || 'خطا در ارسال پاسخ', {
                position: 'top-center',
                autoClose: 3000,
                rtl: true
            });
        }
        finally {
            setIsSendingReply(false);
        }
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setTimeout(() => {
            setView('tabs');
            setActiveTab('new');
            setCurrentThread(null);
            setReplyMessage('');
        }, 300);
    };
    const handleBackToList = () => {
        setView('tabs');
        setCurrentThread(null);
        setReplyMessage('');
    };
    const getStatusBadge = (status) => {
        const statusMap = {
            'Open': { label: 'باز', color: '#3b82f6' },
            'InProgress': { label: 'در حال بررسی', color: '#f59e0b' },
            'Closed': { label: 'بسته شده', color: '#6b7280' }
        };
        const statusInfo = statusMap[status] || { label: status, color: '#6b7280' };
        return (React.createElement("span", { className: 'ticket-status-badge', style: { backgroundColor: statusInfo.color } }, statusInfo.label));
    };
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('fa-IR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };
    const getInitials = (name) => {
        if (!name)
            return '?';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };
    const hasAdminReplied = (thread) => {
        if (!thread)
            return false;
        return thread.messages.some(msg => msg.senderType === 'Admin');
    };
    const translateSubject = (subject) => {
        const subjectMap = {
            'technical': t('support.form.subjects.technical'),
            'feedback': t('support.form.subjects.feedback'),
            'other': t('support.form.subjects.other')
        };
        return subjectMap[subject] || subject;
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("button", { "aria-label": t('support.title'), className: 'floating-support-btn', onClick: () => setIsModalOpen(true), type: 'button' },
            React.createElement("svg", { fill: 'none', height: '28', viewBox: '0 0 256 256', width: '28', xmlns: 'http://www.w3.org/2000/svg' },
                React.createElement("rect", { fill: 'none', height: '256', width: '256' }),
                React.createElement("path", { d: 'M232,128v80a40,40,0,0,1-40,40H136a8,8,0,0,1,0-16h56a24,24,0,0,0,24-24H192a24,24,0,0,1-24-24V144a24,24,0,0,1,24-24h23.65A88,88,0,0,0,66,65.54,87.29,87.29,0,0,0,40.36,120H64a24,24,0,0,1,24,24v40a24,24,0,0,1-24,24H48a24,24,0,0,1-24-24V128A104.11,104.11,0,0,1,201.89,54.66,103.41,103.41,0,0,1,232,128Z', fill: 'currentColor' }))),
        isModalOpen && (React.createElement("div", { className: 'support-modal-overlay', onClick: handleCloseModal },
            React.createElement("div", { className: 'support-modal support-modal--large', onClick: e => e.stopPropagation() },
                React.createElement("button", { className: 'support-modal-close', onClick: handleCloseModal, type: 'button' }, "\u00D7"),
                view === 'tabs' && (React.createElement(React.Fragment, null,
                    React.createElement("h2", { className: 'support-modal-title' }, t('support.title')),
                    React.createElement("div", { className: 'support-tabs' },
                        React.createElement("button", { className: `support-tab ${activeTab === 'new' ? 'active' : ''}`, onClick: () => setActiveTab('new'), type: 'button' }, "\u0627\u0631\u0633\u0627\u0644 \u062A\u06CC\u06A9\u062A \u062C\u062F\u06CC\u062F"),
                        React.createElement("button", { className: `support-tab ${activeTab === 'history' ? 'active' : ''}`, onClick: () => setActiveTab('history'), type: 'button' },
                            "\u062A\u06CC\u06A9\u062A\u200C\u0647\u0627\u06CC \u0645\u0646 ",
                            myTickets.length > 0 && `(${myTickets.length})`)),
                    activeTab === 'new' && (React.createElement("div", { className: 'support-form-wrapper' },
                        !isAuthenticated && (React.createElement("div", { className: 'support-login-overlay' },
                            React.createElement("div", { className: 'support-login-overlay-content' },
                                React.createElement("svg", { fill: 'none', height: '48', viewBox: '0 0 24 24', width: '48', xmlns: 'http://www.w3.org/2000/svg' },
                                    React.createElement("path", { d: 'M12 15V17M6 21H18C19.1046 21 20 20.1046 20 19V13C20 11.8954 19.1046 11 18 11H6C4.89543 11 4 11.8954 4 13V19C4 20.1046 4.89543 21 6 21ZM16 11V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V11H16Z', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' })),
                                React.createElement("p", null, t('support.loginRequired')),
                                React.createElement("button", { className: 'support-submit-btn', onClick: () => { dispatch(openLoginModal()); setIsModalOpen(false); }, type: 'button' }, t('welcomepage.navbar.loginSignup'))))),
                        React.createElement("form", { className: 'support-form', onSubmit: handleSubmit },
                            React.createElement("div", { className: 'form-row' },
                                React.createElement("div", { className: 'form-group' },
                                    React.createElement("label", { htmlFor: 'name' }, t('support.form.name')),
                                    React.createElement("input", { id: 'name', name: 'name', onChange: handleChange, required: true, type: 'text', value: formData.name })),
                                React.createElement("div", { className: 'form-group' },
                                    React.createElement("label", { htmlFor: 'email' }, t('support.form.email')),
                                    React.createElement("input", { id: 'email', name: 'email', onChange: handleChange, required: true, type: 'email', value: formData.email })),
                                React.createElement("div", { className: 'form-group' },
                                    React.createElement("label", { htmlFor: 'phoneNumber' }, t('support.form.phonenumber')),
                                    React.createElement("input", { id: 'phoneNumber', name: 'phoneNumber', onChange: handleChange, required: true, type: 'tel', value: formData.phoneNumber })),
                                React.createElement("div", { className: 'form-group' },
                                    React.createElement("label", { htmlFor: 'subject' }, t('support.form.subject')),
                                    React.createElement("select", { id: 'subject', name: 'subject', onChange: handleChange, required: true, value: formData.subject },
                                        React.createElement("option", { value: '' }, t('support.form.selectSubject')),
                                        React.createElement("option", { value: 'technical' }, t('support.form.subjects.technical')),
                                        React.createElement("option", { value: 'feedback' }, t('support.form.subjects.feedback')),
                                        React.createElement("option", { value: 'other' }, t('support.form.subjects.other'))))),
                            React.createElement("div", { className: 'form-group' },
                                React.createElement("label", { htmlFor: 'message' }, t('support.form.message')),
                                React.createElement("textarea", { id: 'message', maxLength: 10000, name: 'message', onChange: handleChange, required: true, rows: 5, value: formData.message })),
                            React.createElement("button", { className: 'support-submit-btn', disabled: isSubmitting, type: 'submit' }, isSubmitting ? React.createElement("span", { className: 'support-spinner' }) : t('support.form.submit'))))),
                    activeTab === 'history' && (React.createElement("div", { className: 'support-tickets-list' }, isLoadingTickets ? (React.createElement(React.Fragment, null, [1, 2, 3].map(i => (React.createElement("div", { className: 'ticket-skeleton', key: i },
                        React.createElement("div", { className: 'skeleton-line skeleton-line--title' }),
                        React.createElement("div", { className: 'skeleton-line skeleton-line--text' }),
                        React.createElement("div", { className: 'skeleton-line skeleton-line--short' })))))) : myTickets.length === 0 ? (React.createElement("div", { className: 'support-empty' },
                        React.createElement("svg", { fill: 'none', height: '64', viewBox: '0 0 24 24', width: '64', xmlns: 'http://www.w3.org/2000/svg' },
                            React.createElement("path", { d: 'M9 12H15M12 9V15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z', stroke: '#ccc', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' })),
                        React.createElement("p", null, "\u0634\u0645\u0627 \u0647\u0646\u0648\u0632 \u062A\u06CC\u06A9\u062A\u06CC \u062B\u0628\u062A \u0646\u06A9\u0631\u062F\u0647\u200C\u0627\u06CC\u062F"))) : (myTickets.map(ticket => (React.createElement("div", { className: 'ticket-item', key: ticket.id, onClick: () => handleTicketClick(ticket) },
                        React.createElement("div", { className: 'ticket-header' },
                            React.createElement("h4", { className: 'ticket-subject' }, translateSubject(ticket.subject)),
                            getStatusBadge(ticket.status)),
                        React.createElement("div", { className: 'ticket-meta' },
                            React.createElement("span", { className: 'ticket-code' },
                                "\u06A9\u062F: ",
                                ticket.referenceCode),
                            React.createElement("span", { className: 'ticket-date' }, formatDate(ticket.createdAtUtc))),
                        React.createElement("div", { className: 'ticket-footer' },
                            React.createElement("span", { className: 'ticket-messages' },
                                ticket.messageCount,
                                " \u067E\u06CC\u0627\u0645")))))))))),
                view === 'thread' && (React.createElement("div", { className: 'support-thread' }, isLoadingThread ? (React.createElement("div", { className: 'support-loading' },
                    React.createElement("span", { className: 'support-spinner' }),
                    React.createElement("p", null, "\u062F\u0631 \u062D\u0627\u0644 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC..."))) : (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: 'support-thread-header' },
                        React.createElement("button", { className: 'back-btn', onClick: handleBackToList, type: 'button' },
                            React.createElement("svg", { fill: 'none', height: '20', viewBox: '0 0 24 24', width: '20', xmlns: 'http://www.w3.org/2000/svg' },
                                React.createElement("path", { d: 'M19 12H5M12 19L5 12L12 5', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' }))),
                        React.createElement("div", { className: 'thread-info' },
                            React.createElement("h3", null, currentThread?.subject),
                            React.createElement("span", { className: 'thread-code' },
                                "\u06A9\u062F: ",
                                currentThread?.referenceCode)),
                        currentThread && getStatusBadge(currentThread.status)),
                    React.createElement("div", { className: 'support-messages' },
                        currentThread && (React.createElement("div", { className: 'message message-user', key: 'initial-message' },
                            React.createElement("div", { className: 'message-avatar' }, currentThread.name ? getInitials(currentThread.name) : 'ش'),
                            React.createElement("div", { className: 'message-bubble' },
                                React.createElement("div", { className: 'message-header' },
                                    React.createElement("span", { className: 'message-sender' }, "\u0634\u0645\u0627"),
                                    React.createElement("span", { className: 'message-time' }, formatDate(currentThread.createdAtUtc))),
                                React.createElement("div", { className: 'message-content' }, currentThread.message)))),
                        currentThread?.messages.map(msg => (React.createElement("div", { className: `message ${msg.senderType === 'User' ? 'message-user' : 'message-admin'}`, key: msg.id },
                            React.createElement("div", { className: 'message-avatar' }, msg.senderType === 'Admin'
                                ? (msg.adminName ? getInitials(msg.adminName) : 'د')
                                : (currentThread?.name ? getInitials(currentThread.name) : 'ش')),
                            React.createElement("div", { className: 'message-bubble' },
                                React.createElement("div", { className: 'message-header' },
                                    React.createElement("span", { className: 'message-sender' }, msg.senderType === 'Admin' ? (msg.adminName || 'پشتیبانی دیدار') : 'شما'),
                                    React.createElement("span", { className: 'message-time' }, formatDate(msg.createdAtUtc))),
                                React.createElement("div", { className: 'message-content' }, msg.message)))))),
                    currentThread?.status === 'Closed' ? (React.createElement("div", { className: 'support-closed-notice' }, "\u0627\u06CC\u0646 \u062A\u06CC\u06A9\u062A \u0628\u0633\u062A\u0647 \u0634\u062F\u0647 \u0627\u0633\u062A \u0648 \u0627\u0645\u06A9\u0627\u0646 \u0627\u0631\u0633\u0627\u0644 \u067E\u0627\u0633\u062E \u0648\u062C\u0648\u062F \u0646\u062F\u0627\u0631\u062F.")) : hasAdminReplied(currentThread) ? (React.createElement("div", { className: 'support-reply' },
                        React.createElement("textarea", { maxLength: 2000, onChange: e => setReplyMessage(e.target.value), placeholder: '\u067E\u0627\u0633\u062E \u062E\u0648\u062F \u0631\u0627 \u0628\u0646\u0648\u06CC\u0633\u06CC\u062F...', rows: 3, value: replyMessage }),
                        React.createElement("button", { className: 'support-submit-btn', disabled: isSendingReply || !replyMessage.trim(), onClick: handleSendReply, type: 'button' }, isSendingReply ? React.createElement("span", { className: 'support-spinner' }) : 'ارسال پاسخ'))) : (React.createElement("div", { className: 'support-waiting-notice' }, "\u062A\u06CC\u06A9\u062A \u0634\u0645\u0627 \u062B\u0628\u062A \u0634\u062F\u0647 \u0627\u0633\u062A. \u0644\u0637\u0641\u0627\u064B \u0645\u0646\u062A\u0638\u0631 \u067E\u0627\u0633\u062E \u062A\u06CC\u0645 \u067E\u0634\u062A\u06CC\u0628\u0627\u0646\u06CC \u0628\u0627\u0634\u06CC\u062F.")))))))))));
};
export default FloatingSupportButton;
